import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

const SESSION_KEY = 'ftn_analytics_session';
const SESSION_STARTED_KEY = 'ftn_analytics_session_started';

@Injectable({
  providedIn: 'root'
})
export class WebsiteSessionService {
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private sessionStartedAt = Date.now();

  constructor(private http: HttpClient) {}

  init(): void {
    this.ensureSessionId();
    this.sessionStartedAt = this.getSessionStartedAt();
    this.recordSession();
    this.startHeartbeat();

    window.addEventListener('beforeunload', () => this.flushSession());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flushSession();
      }
    });
  }

  getSessionId(): string {
    return this.ensureSessionId();
  }

  private ensureSessionId(): string {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = this.generateId();
      localStorage.setItem(SESSION_KEY, id);
      localStorage.setItem(SESSION_STARTED_KEY, Date.now().toString());
    }
    return id;
  }

  private getSessionStartedAt(): number {
    const stored = localStorage.getItem(SESSION_STARTED_KEY);
    return stored ? parseInt(stored, 10) : Date.now();
  }

  private getDurationSeconds(): number {
    return Math.max(0, Math.floor((Date.now() - this.sessionStartedAt) / 1000));
  }

  private startHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
    }
    this.heartbeatTimer = setInterval(() => this.recordSession(), 30000);
  }

  private recordSession(): void {
    const payload = {
      sessionId: this.getSessionId(),
      durationSeconds: this.getDurationSeconds(),
    };
    this.http.post(`${environment.apiBaseUrl}Analytics/RecordWebsiteSession`, payload).subscribe({
      error: () => { /* silent */ }
    });
  }

  private flushSession(): void {
    const payload = JSON.stringify({
      sessionId: this.getSessionId(),
      durationSeconds: this.getDurationSeconds(),
    });
    const url = `${environment.apiBaseUrl}Analytics/RecordWebsiteSession`;
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
    }
  }

  private generateId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
}
